import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { AccSpecialistFuncData } from '@interfaces';
import { DomainDataService } from 'services';
import { AcceleratorAreaService } from '../../../../accelerator-area.service';
import { AccCohortPageService, } from '../../../acc-cohort.page.service';
import { getSetup } from './grid/get-setup';

export interface AccSpecialist extends AccSpecialistFuncData {
	approvedLanguage: string,
	approvedTopics: string,
	assignedTopics: string,
	assignedEventsCount: number,
	updatedUTC: number,
}

@Component({
	selector: 'acc-specialists-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './acc-specialists-view.part.html',
})
export class AccSpecialistsViewPart {

	private readonly util = inject(UtilityService);
	private readonly dialogService = inject(DialogService);
	private readonly pageService = inject(AccCohortPageService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domainDataService = inject(DomainDataService);

	public setup = getSetup(this.util, this.dialogService, this.areaService);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public accSpecialists = computed<AccSpecialist[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		const topicsMap = this.domainDataService.data().topicsMap;
		const accTopicMap = this.util.array.toMap(data.accTopics, t => t.accTopicId);
		const accSessionTopicMap = this.util.array.toMap(data.sessionTopics, ast => ast.accTopicId);
		const accSessionMap = this.util.array.toMap(data.sessions, s => s.accSessionId);

		const topicSpecialistsMap = this.util.array.toArrayMap(data.topicSpecialists, ts => ts.personId);

		return data.specialists.map(specialist => {

			const specialistAccTopics = topicSpecialistsMap[specialist.personId]?.map(ts => ts.accTopicId) ?? [];

			const assignedTopics: string[] = [];
			const assignedEventIds: number[] = [];

			specialistAccTopics.forEach(accTopicId => {
				const accTopic = accTopicMap[accTopicId];
				if (!accTopic) return;

				assignedTopics.push(topicsMap[accTopic.topicId]!.name);

				// Find events via session topics and sessions
				const accSessionTopic = accSessionTopicMap[accTopicId];
				if (!accSessionTopic) return;

				const accSession = accSessionMap[accSessionTopic.accSessionId];
				if (!accSession) return;

				if (accSession.eventId) assignedEventIds.push(accSession.eventId);

			});

			let language = '';
			if (specialist.canVolunteerInEnglish && specialist.canVolunteerInSpanish) {
				language = 'Both';
			} else if (specialist.canVolunteerInEnglish) {
				language = 'English';
			} else if (specialist.canVolunteerInSpanish) {
				language = 'Spanish';
			}

			return {
				...specialist,
				approvedTopics: specialist.approvedTopicIds.map(id => topicsMap[id]!.name).join(', '),
				assignedTopics: assignedTopics.join(', '),
				assignedEventsCount: assignedEventIds.length,
				approvedLanguage: language,
				updatedUTC: specialist.updatedUTC ?? data.asOfUTC,
			}
		});

	});

}